import { useMemo } from 'react';
import { useCultureHubQueryParams } from '../../../hooks/useCultureHubQueryParams';
import { useGetCultureHubAnalyticsQuery } from '../../../api/cultureHubApi';


export const useDashboardCardsData = () => {
  const { assessmentPeriodId, teamId, shouldFetchData } = useCultureHubQueryParams();

  const { data: analyticsData, isSuccess } = useGetCultureHubAnalyticsQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId },
    { skip: !assessmentPeriodId || !shouldFetchData || !teamId }
  );

  const pillarScores = analyticsData?.pillar_scores;
  const outcomeScores = analyticsData?.outcome_scores;
  const historicalMetrics = analyticsData?.historical_performance_metrics;

  const isTeamView = (teamId !== null && teamId !== undefined) || (outcomeScores !== null && outcomeScores !== undefined);

  return useMemo(() => {
    // Keep loading only until the analytics query has returned.
    // Some historical/snapshot-backed teams may not include pillar_scores.
    if (!isSuccess) {
      return {
        pillarDeltas: null as null | Array<{ label: string; currentScore: number; previousScore: number | null; delta: number | null }>,
        outcomeItems: null as null | Array<{ label: string; score: number; delta: number | null }>,
        isTeamView,
        overallScore: null as number | null,
        differential: null as number | null,
        previousScore: null as number | null,
        isLoaded: false,
      };
    }

    const pillarsArrayFromScores = Array.isArray(pillarScores?.pillars) ? pillarScores.pillars : [];
    const pillarsArray = pillarsArrayFromScores.length > 0
      ? pillarsArrayFromScores
      : Object.values(analyticsData?.pillars || {}).map((pillar: any) => ({
          label: pillar?.name || 'Unknown Pillar',
          average_response: Number(pillar?.score ?? 0),
          delta: typeof pillar?.trend === 'number' ? Number(pillar.trend) : null,
          previous_score:
            typeof pillar?.trend === 'number' && typeof pillar?.score === 'number'
              ? Number(pillar.score) - Number(pillar.trend)
              : null,
        }));

    const pillarDeltas = pillarsArray.map((item: {
      label: string;
      average_response: number;
      delta?: number | null;
      previous_score?: number | null;
    }) => {
      const currentScore = parseFloat(item.average_response.toFixed(1));
      const delta = item.delta !== undefined && item.delta !== null
        ? parseFloat(item.delta.toFixed(1))
        : null;
      const previousScore = item.previous_score !== undefined && item.previous_score !== null
        ? parseFloat(item.previous_score.toFixed(1))
        : null;

      return {
        label: item.label,
        currentScore,
        previousScore,
        delta,
      };
    });

    let outcomeItems: Array<{ label: string; score: number; delta: number | null }> = [];
    if (isTeamView && outcomeScores?.outcomes && Array.isArray(outcomeScores.outcomes) && outcomeScores.outcomes.length > 0) {
      outcomeItems = outcomeScores.outcomes.map((outcome: {
        label?: string;
        average_response?: number;
        delta?: number | null;
      }) => {
        const score = parseFloat((outcome.average_response || 0).toFixed(1));
        const delta = outcome.delta !== undefined && outcome.delta !== null
          ? parseFloat(outcome.delta.toFixed(1))
          : null;

        return {
          label: outcome.label || 'Unknown Outcome',
          score,
          delta,
        };
      });
    }

    const currentScore = typeof pillarScores?.overall_score === 'number'
      ? pillarScores.overall_score
      : (pillarsArray.length > 0
          ? pillarsArray.reduce((sum: number, p: any) => sum + Number(p.average_response || 0), 0) / pillarsArray.length
          : 0);

    // Derive previous score from historical_performance_metrics
    // The array is chronological, so the second-to-last entry is the previous period
    let previousScore: number | null = null;
    if (historicalMetrics && historicalMetrics.length >= 2) {
      const previousPeriod = historicalMetrics[historicalMetrics.length - 2];
      previousScore = previousPeriod?.overall_score ?? null;
    }

    const differential = previousScore !== null
      ? parseFloat((currentScore - previousScore).toFixed(1))
      : null;

    return {
      pillarDeltas,
      outcomeItems,
      isTeamView,
      overallScore: currentScore,
      differential,
      previousScore,
      isLoaded: true,
    };
  }, [isSuccess, pillarScores, outcomeScores, historicalMetrics, isTeamView, analyticsData?.pillars]);
};
